import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { locales } from '@mdt/i18n';
import { pseudoTree } from './i18n-pseudo.ts';

/**
 * Bilingual UI (roadmap 3.2): the catalogs of packages/i18n (key parity is a CI gate). Thai is the default.
 * The development and e2e builds also carry a pseudo-locale (`?lng=pseudo`): every English string grows by
 * about 50 % in accented Latin, so a label that would be cut off or overflow in a longer translation shows
 * up in the overflow check before any translator touches it.
 */
export type UiLocale = 'th' | 'en';
export const LOCALE_STORAGE_KEY = 'mdt.locale';

type Tree = { [k: string]: string | Tree };

function readStored(): UiLocale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return v === 'th' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

function initialLanguage(): string {
  if (__MDT_TEST_PAGES__ && new URLSearchParams(globalThis.location?.search ?? '').get('lng') === 'pseudo') return 'pseudo';
  return readStored() ?? 'th';
}

const resources: Record<string, { translation: Tree }> = {
  th: { translation: locales.th as unknown as Tree },
  en: { translation: locales.en as unknown as Tree },
};
if (__MDT_TEST_PAGES__) resources.pseudo = { translation: pseudoTree(locales.en as unknown as Tree) };

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: 'th',
  interpolation: { escapeValue: false },   // React escapes; nothing is ever set as HTML
  returnNull: false,
});

function applyDocumentLanguage(lng: string) {
  document.documentElement.lang = lng === 'th' ? 'th' : 'en';
}
applyDocumentLanguage(i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);

export function currentLocale(): UiLocale {
  return i18n.language === 'th' ? 'th' : 'en';
}

export function setLocale(l: UiLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, l);
  } catch {
    // private mode: the choice lasts for this page only
  }
  if (i18n.language !== 'pseudo') void i18n.changeLanguage(l);
}

/** A message key from the server (or the client) as text; an unknown key falls back to a generic error. */
export function messageText(key: string): string {
  return i18n.exists(key) ? i18n.t(key) : i18n.t('err.internal');
}

export default i18n;
